import React, { useEffect, useRef, useId, useCallback } from 'react';

const scriptPromises = new Map();

function loadRecaptchaScript(siteKey, type) {
  if (typeof window === 'undefined') return Promise.resolve();
  const key = type === 'v3' ? `v3:${siteKey}` : 'v2';
  if (window.grecaptcha?.render || (type === 'v3' && window.grecaptcha?.execute)) {
    return Promise.resolve();
  }
  const existing = scriptPromises.get(key);
  if (existing) return existing;

  const promise = new Promise((resolve, reject) => {
    const src =
      type === 'v3'
        ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
        : 'https://www.google.com/recaptcha/api.js?render=explicit';

    const existingScript = document.querySelector(`script[data-recaptcha="${key}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')));
      return;
    }

    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = key;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
    document.head.appendChild(s);
  });
  scriptPromises.set(key, promise);
  return promise;
}

export default function RecaptchaWidget({
  siteKey,
  type = 'v2_checkbox',
  action = 'auth',
  onToken,
  onExpire,
  className,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const domId = useId().replace(/:/g, '');

  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;

  const runV3 = useCallback(async () => {
    if (!window.grecaptcha?.execute) return;
    try {
      const token = await window.grecaptcha.execute(siteKey.trim(), { action });
      if (token) onTokenRef.current?.(token);
    } catch {
      onExpireRef.current?.();
    }
  }, [siteKey, action]);

  useEffect(() => {
    if (!siteKey?.trim()) return undefined;

    let cancelled = false;
    let refreshTimer;

    loadRecaptchaScript(siteKey.trim(), type)
      .then(() => {
        if (cancelled || !window.grecaptcha) return;

        window.grecaptcha.ready(() => {
          if (cancelled) return;

          if (type === 'v3') {
            void runV3();
            refreshTimer = setInterval(() => void runV3(), 90_000);
            return;
          }

          if (!containerRef.current) return;
          if (widgetIdRef.current != null) {
            try {
              window.grecaptcha.reset(widgetIdRef.current);
            } catch {
              /* ignore */
            }
          }

          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey.trim(),
            size: type === 'v2_invisible' ? 'invisible' : 'normal',
            callback: (token) => onTokenRef.current?.(token),
            'expired-callback': () => onExpireRef.current?.(),
            'error-callback': () => onExpireRef.current?.(),
          });

          if (type === 'v2_invisible' && widgetIdRef.current != null) {
            void window.grecaptcha.execute(widgetIdRef.current).catch(() => onExpireRef.current?.());
            refreshTimer = setInterval(() => {
              if (widgetIdRef.current == null || !window.grecaptcha) return;
              try {
                window.grecaptcha.reset(widgetIdRef.current);
                void window.grecaptcha.execute(widgetIdRef.current);
              } catch {
                onExpireRef.current?.();
              }
            }, 110_000);
          }
        });
      })
      .catch(() => onExpireRef.current?.());

    return () => {
      cancelled = true;
      if (refreshTimer) clearInterval(refreshTimer);
      widgetIdRef.current = null;
    };
  }, [siteKey, type, action, runV3]);

  if (!siteKey?.trim()) return null;

  if (type === 'v3') {
    return <div className={className} aria-hidden="true" data-recaptcha="v3" />;
  }

  return (
    <div
      ref={containerRef}
      id={`recaptcha-${domId}`}
      className={className}
      style={type === 'v2_invisible' ? { height: 0, overflow: 'hidden' } : undefined}
    />
  );
}
